<script setup>

import { computed, onBeforeMount, onMounted, onUnmounted } from "vue";
import FlatPickr from "vue-flatpickr-component";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import "flatpickr/dist/flatpickr.css";
import { useStore } from "vuex";
import setTooltip from "../assets/js/tooltip.js";
import axios from "axios";
import bootstrap from "bootstrap/dist/js/bootstrap";
import { toast } from 'vue3-toastify';
import loadGif from "@/assets/img/loading.gif"
import 'animate.css';
import calculator from "./calculator.vue";
import emitter from "../assets/js/eventBus.js";
import cookies from "../assets/js/cookies.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const store = useStore();

const darkMode = computed(() => store.state.darkMode);


const flatpickrConfig = computed(() => ({
    dateFormat: "Y-m-d",
    altInput: true,
    altFormat: "d/m/Y",
    allowInput: false,
    locale: Portuguese,
    disableMobile: false,
    monthSelectorType: "static"
}));

const accountOptions = computed(() => ([
    ...((store.state.accounts || []).map(account => ({
        value: `${account.id}-bank`,
        name: account.name,
        icon: account.icon || "",
        meta: "Conta",
        raw: account,
    }))),
    ...((store.state.cards || []).map(card => ({
        value: `${card.id}-card`,
        name: card.name,
        icon: card.icon || "",
        meta: "Cartão",
        raw: card,
    })))
]));

const bankOnlyOptions = computed(() => (
    (store.state.accounts || []).map(account => ({
        value: `${account.id}-bank`,
        name: account.name,
        icon: account.icon || "",
        meta: "Conta",
        raw: account,
    }))
));

const transactionAccountOptions = computed(() => {
    const transactionType = store.state.transation?.type;
    if (transactionType === 'receita') {
        return bankOnlyOptions.value;
    }
    if (transactionType === 'despesa') {
        return accountOptions.value;
    }
    return accountOptions.value;
});

const selectedTransactionAccountOption = computed({
    get() {
        const currentValue = store.state.transation?.account;
        if (currentValue === null || currentValue === undefined || currentValue === '') return null;
        return transactionAccountOptions.value.find(option => String(option.value) === String(currentValue)) || null;
    },
    set(option) {
        store.state.transation.account = option?.value ?? '';
    }
});


const flattenCategories = (items = [], level = 0) => {
    const source = Array.isArray(items) ? items : Object.values(items || {});
    return source.flatMap(category => {
        const option = {
            value: category.id,
            name: `${level > 0 ? "— ".repeat(level) : ""}${category.name}`,
            cleanName: category.name,
            icon: category.icon || "fa-tag",
            color: category.color || "#94a3b8",
            raw: category,
        };

        const children = flattenCategories(category.subs || [], level + 1);
        return [option, ...children];
    });
};

const categoryOptions = computed(() => {
    const transactionType = store.state.transation?.type;
    const source =
        transactionType === 'receita'
            ? store.state.categoriesRevenue
            : transactionType === 'despesa'
                ? store.state.categoriesExpense
                : {};
    return flattenCategories(source);
});

const objetiveOptions = computed(() => (
    (store.state.objetives || []).map(objetive => ({
        value: objetive.id,
        name: objetive.name,
        icon: objetive.icon || "fa-bullseye",
        color: objetive.color || "#64748b",
        raw: objetive,
    }))
));

const customerSupplierOptions = computed(() => ([
    ...((store.state.customers || []).map(customer => ({
        value: customer.id,
        name: customer.name,
        meta: "Cliente",
    }))),
    ...((store.state.suppliers || []).map(supplier => ({
        value: supplier.id,
        name: supplier.name,
        meta: "Fornecedor",
    })))
]));

const multiselectClasses = computed(() => ({
    container: `multiselect transaction-multiselect ${darkMode.value ? 'is-darkmode' : ''}`,
    containerActive: `is-active`,
    wrapper: `multiselect-wrapper`,
    singleLabel: `multiselect-single-label`,
    search: `multiselect-search`,
    dropdown: `multiselect-dropdown`,
    option: `multiselect-option`,
    optionSelected: `is-selected`,
    optionPointed: `is-pointed`,
    placeholder: `multiselect-placeholder`,
    tag: `multiselect-tag`,
    clear: `multiselect-clear`,
    caret: `multiselect-caret`,
}));

const findOptionByValue = (options, value) => (options || []).find(option => String(option.value) === String(value)) || null;

const selectedAccountOption = computed({
    get: () => findOptionByValue(accountOptions.value, store.state.transation.account),
    set: (option) => {
        store.state.transation.account = option?.value || '';
    }
});

const selectedBankAccountOption = computed({
    get: () => findOptionByValue(bankOnlyOptions.value, store.state.transation.account),
    set: (option) => {
        store.state.transation.account = option?.value || '';
    }
});

const selectedDestinyAccountOption = computed({
    get: () => findOptionByValue(bankOnlyOptions.value, store.state.transation.account_destiny),
    set: (option) => {
        store.state.transation.account_destiny = option?.value || '';
    }
});

const selectedCategoryOption = computed({
    get: () => findOptionByValue(categoryOptions.value, store.state.transation.category),
    set: (option) => {
        store.state.transation.category = option?.value || '';
    }
});

const selectedObjetiveOption = computed({
    get: () => findOptionByValue(objetiveOptions.value, store.state.transation.objetive),
    set: (option) => {
        store.state.transation.objetive = option?.value || '';
    }
});

const selectedCustomerSupplierOption = computed({
    get: () => findOptionByValue(customerSupplierOptions.value, store.state.transation.customer_supplier),
    set: (option) => {
        store.state.transation.customer_supplier = option?.value || '';
    }
});

const selectedPaymentAccountOption = computed({
    get: () => findOptionByValue(accountOptions.value, store.state.transation.account),
    set: (option) => {
        store.state.transation.account = option?.value || '';
    }
});


const money = {
          decimal: ',',
          thousands: '.',
          prefix: 'R$ ',
          suffix: '',
          precision: 2,
          masked: false /* doesn't work with directive */
        }

onBeforeMount(() => {

    let session = cookies.get('user_agive_session');

    store.state.is_card = false;

    store.state.transaction_value = {
        value: 0
    }

    store.state.accounts = [];
    store.state.cards = [];
    store.state.categoriesExpense = {};
    store.state.categoriesRevenue = {};
    store.state.savingTransaction = false;
    store.state.modalColor = '';
    store.state.objetives = [];
    store.state.suggestions = [];
    store.state.customers = [];
    store.state.suppliers = [];

    store.state.validation = {
        descHasError: false,
        descMessageError: '',
    };

    let day = new Date().getDate();
    let month = new Date().getMonth() + 1;
    let year = new Date().getFullYear();

    store.state.current_date = year+ '-' + (month < 10 ? '0'+month : month) + '-' + (day < 10 ? '0'+ day : day);

    store.state.transation = {
        type: '',
        description: '',
        amount: 0,
        date: store.state.current_date,
        account: '',
        account_name: '',
        account_destiny: '',
        account_destiny_name: '',
        category: '',
        category_name: '',
        category_icon: '',
        category_color: '',
        recurrence_checked: false,
        recurrence: '',
        recurrence_period: 'monthly',
        initial_installment: 1,
        current_installment: 1,
        end_installment: 1,
        payed: false,
        id: '',
        transaction_id: '',
        invoice_id: '',
        card_id: '',
        howUpdate: '',
        amount_paid: 0,
        ammount_unpaid: 0,
        observation: '',
        objetive_checked: false,
        objetive: '',
        customer_supplier: '',
        customer_supplier_name: ''
    }

    if(session !== null){

        getCards();
        getAccounts();
        getExpenseCategories();
        getRevenueCategories();
        getObjetives();
        get_customers_suppliers();

    }

})

onMounted(() => {

    emitter.on('set-type', setType);
    emitter.on('edit-transaction', editTrasaction);
    emitter.on('delete-transaction', deleteTransation);
    emitter.on('receive-data', (res) => {

        store.state.transation.account = '';
        store.state.is_card = false;
        store.state.transation.payed = false;

        let fullURL = window.location.href;

        if(fullURL.indexOf('fatura') > -1){

            store.state.transation.account = res.data;
            store.state.is_card = true;
        }
        
    });
    
    setTooltip();

});

onUnmounted(() => {
    emitter.off('set-type', setType);
    emitter.off('edit-transaction', editTrasaction);
    emitter.off('delete-transaction', deleteTransation);
    emitter.off('receive-data', () => {});
});

const getAccounts = async () => {

    axios.get('accounts?archived=No')
    .then(res => {
        
        if(res.status == 200){

            store.state.accounts = res.data;

        }
        
    })
    .catch(err => {
        console.error(err); 
    })

}

const getCards = async () => {

    axios.get('credit-cards')
    .then(res => {
        
        if(res.status == 200){

            store.state.cards = res.data;

        }
        
    })
    .catch(err => {
        console.error(err); 
    })

}

const getExpenseCategories = async () => {

    axios.get('categories?type=despesa')
    .then(res => {
        
        if(res.status == 200){

            store.state.categoriesExpense = res.data;

        }
        
    })
    .catch(err => {
        console.error(err); 
    })

}

const getRevenueCategories = async () => {

    axios.get('categories?type=receita')
    .then(res => {

    if(res.status == 200){

        store.state.categoriesRevenue = res.data;

    }

    })
    .catch(err => {
        console.error(err); 
    })

}

const setType = (type, is_card = false) => {

    store.state.is_card = is_card;

    emitter.emit('get-data', { data: '' });

    if (store.state.transation.type !== type.data) {
        store.state.transation.category = '';
        store.state.transation.category_name = '';
        store.state.transation.category_icon = '';
        store.state.transation.category_color = '';
    }

    if(type.data == 'despesa'){
        store.state.modalColor = '-danger';
    }
    else if(type.data == 'receita'){
        store.state.modalColor = '-success';
    }
    else{
        store.state.modalColor = '-warning';
        store.state.transation.description = 'Transferência';
        store.state.transation.payed = true;
        store.state.transation.category = 2;
    }

    store.state.transation.type = type.data;

}

const clearFields = () => {

    store.state.transaction_value.value = 0

    store.state.transation = {
        type: '',
        description: '',
        amount: 0,
        date: store.state.current_date,
        account: '',
        account_name: '',
        account_destiny: '',
        account_destiny_name: '',
        category: '',
        category_name: '',
        category_icon: '',
        category_color: '',
        recurrence_checked: false,
        recurrence: '',
        recurrence_period: 'monthly',
        initial_installment: 1,
        current_installment: 1,
        end_installment: 1,
        payed: false,
        id: '',
        transaction_id: '',
        invoice_id: '',
        card_id: '',
        howUpdate: '',
        amount_paid: 0,
        ammount_unpaid: 0,
        observation: '',
        objetive: '',
        customer_supplier: '',
        customer_supplier_name: ''
    }

    store.state.suggestions = [];
}

const editTrasaction = (res) => {

    let data = res.data;

    store.state.transaction_value.value = formatMoney(data.amount);

    store.state.transation = {
        type: data.type,
        description: data.description,
        amount: formatMoney(data.amount),
        date: data.date,
        account: data.account_id > 0 ? data.account_id+'-bank': data.card_id+'-card',
        account_name: data.account_name,
        account_destiny: data.account_destiny_id > 0 ? data.account_destiny_id+'-bank': '',
        account_destiny_name: data.account_destiny_name,
        category: data.category_id,
        category_name: data.category_name,
        category_icon: data.category_icon,
        category_color: data.category_color,
        recurrence_checked: false,
        recurrence: data.recurrence,
        recurrence_period: '',
        initial_installment: 1,
        current_installment: data.current_installment,
        end_installment: data.end_installment,
        payed: data.status == 'Unpaid' ? false : true,
        id: data.id,
        invoice_id: data.invoice_id,
        card_id: data.card_id,
        transaction_id: data.transaction_id,
        howUpdate: 'just_this',
        amount_paid: data.amount_paid,
        ammount_unpaid: data.ammount_unpaid,
        observation: data.observation,
        objetive: data.objetive_id,
        objetive_checked: data.objetive_id > 0 ? true : false,
        customer_supplier: data.customer_suplier_id,
        customer_supplier_name: data.customer_supplier_name
    }

}

const submitTransaction = async () => {

    store.state.savingTransaction = true;

    let data = new FormData();

    store.state.validation.descHasError = false;

    store.state.transation.amount = store.state.transaction_value.value.replace('R$ ', '');

    data.append('type', store.state.transation.type);
    data.append('description', store.state.transation.description);
    data.append('amount', store.state.transation.amount);
    data.append('date', store.state.transation.date);
    data.append('account', store.state.transation.account);
    data.append('account_destiny', store.state.transation.account_destiny);
    data.append('category', store.state.transation.category);
    data.append('recurrence', store.state.transation.recurrence);
    data.append('period', store.state.transation.recurrence_period);
    data.append('initial_installment', store.state.transation.initial_installment);
    data.append('end_installment', store.state.transation.end_installment);
    data.append('payed', store.state.transation.payed ? 'Paid' : '');
    data.append('observation', store.state.transation.observation);
    data.append('objetive', store.state.transation.objetive);
    data.append('customer_supplier', store.state.transation.customer_supplier)

    let url = '/transactions';
    let toast_message = t('message.transactions.created');

    if(store.state.transation.id != ''){

        url = '/transactions/update/'+store.state.transation.id;
        toast_message = t('message.transactions.updated');

        if(store.state.transation.account_destiny != ''){

            data['category'] = 2;

        }

        data.append('transaction_id', store.state.transation.transaction_id);
        data.append('howUpdate', store.state.transation.howUpdate);
        data.append('invoice_id', store.state.transation.invoice_id);
    }

    await axios.post(url, data)
    .then(res => {

        if(res.data.error){

            let messages = res.data.messages;

            Object.entries(messages).forEach((value) => {

                if(value[0] == 'description'){
                    store.state.validation.descHasError = true;
                    store.state.validation.descMessageError = value[1];
                }
                else if(value[0] == 'account'){
                    store.state.validation.accountError = true;
                    store.state.validation.accountMessageError = value[1];
                }
                else if(value[0] == 'category'){
                    store.state.validation.categorryError = true;
                    store.state.validation.categoryMessageError = value[1];
                }

            })

            store.state.savingTransaction = false;

        }
        else{

            store.state.validation = {
                descHasError: false,
                descMessageError: '',
            };

            closeModal('addTransaction');

            toast(toast_message, {
                autoClose: 1000,
                theme: "auto",
                type: "success",
                transition: "slide"
            });

            store.state.savingTransaction = false;

            let retorno = {
                editing: false,
                transaction_id: ''
            };

            if(store.state.transation.id != ''){
                retorno.editing = true;
                retorno.transaction_id = store.state.transation.id;
            }

            emitter.emit('load-data', retorno);
            closeModal('payTransaction');
            clearFields();

        }

    })
    .catch(err => {
        console.error(err); 

        toast('Não foi possível realizar a operação, tente mais tarde (Erro '+err.response.status+')', {
            autoClose: 1000,
            theme: "auto",
            type: "error",
            transition: "slide"
        });

        store.state.savingTransaction = false;

    })

}

const deleteTransation = (id, transaction_id, type) => {

    let url = '/transactions/delete/'+id;

    let data = new FormData();

    data.append('howDelete', type);
    data.append('transaction_id', transaction_id);

    axios.post(url, data)
    .then(res => {
        
        if(!res.data.error){

            toast(t('message.transactions.deleted'), {
                autoClose: 1000,
                theme: "auto",
                type: "success",
                transition: "slide"
            });

            closeModal('deleteTransaction');
            emitter.emit('load-data', { data: {editing:true, transaction_id:transaction_id} });
            clearFields();

        }

    })
    .catch(err => {
        console.error(err); 
    })

}

const closeModal = (id) => {

    let modal = bootstrap.Modal.getOrCreateInstance(document.getElementById(id));

    modal.hide();

}

const formatMoney = (v) => {
    let total = parseFloat(v);

    return total.toLocaleString(t('message.global.date_language'), {
            style: "currency",
            currency: "BRL",
        });
}

const updateValue = (value) => {
    store.state.transaction_value.value = formatMoney(value);
}

const getObjetives = async () => {

    axios.get('objetives')
    .then(res => {
        
        if(res.status == 200){

            store.state.objetives = res.data;

        }
        
    })
    .catch(err => {
        console.error(err); 
    })

}

const autoComplete = async () => {

    if(store.state.transation.description.length >= 3 && store.state.transation.id == ''){

        let url = 'transactions/suggestion/'+store.state.transation.type+'/'+store.state.transation.description;

        if(store.state.is_card){
            url = url + '?isCard=Yes';
        }

        axios.get(url)
        .then(res => {
            
            if(res.status == 200){

                store.state.suggestions = res.data;

            }
            
        })
        .catch(err => {
            console.error(err); 
        })

    }
    else{
        store.state.suggestions = [];
    }

}

const selectAutoComplete = (suggestion) => {

    store.state.transation.description = suggestion.description;
    store.state.transaction_value.value = formatMoney(suggestion.amount);
    store.state.transation.category = suggestion.category_id;
    store.state.transation.account = suggestion.card_id == null ? suggestion.account_id+'-bank': suggestion.card_id+'-card';

    if(suggestion.objetive_id > 0){

        store.state.transation.objetive_checked = true;
        store.state.transation.objetive = suggestion.objetive_id;

    }

    store.state.suggestions = [];

}

const closeAutoComplete = () => {

    store.state.suggestions = [];

}

const formatDate = (d) => {
    
    let date = new Date(d);
    
    return date.toLocaleDateString(t('message.global.date_language'), {timeZone: 'UTC'});

}

const get_customers_suppliers = () => {

    axios.get('customers-suppliers?type=C')
        .then(res => {
            
            if(res.status == 200){

                store.state.customers = res.data;

            }
            
        })
        .catch(err => {
            console.error(err); 
        })

    axios.get('customers-suppliers?type=S')
        .then(res => {
            
            if(res.status == 200){

                store.state.suppliers = res.data;

            }
            
        })
        .catch(err => {
            console.error(err); 
        })

}

const addLinebreaks = (anyString) => {
        if(anyString){
            return anyString.replaceAll("\n", "<br />\r\n");
        }
        
    }

</script>
<template>

    <div class="modal fade" id="addTransaction" tabindex="-1" aria-labelledby="addTransaction" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-transaction-dialog">
            <div class="modal-content border-0 transaction-modal" v-bind:class="{
                        'darkmode': darkMode,
                        'border-danger': store.state.transation.type == 'despesa',
                        'border-success': store.state.transation.type == 'receita',
                        'border-warning': store.state.transation.type == 'transferência'
                    }">

                <div class="modal-header transaction-modal__header">
                    <b v-if="store.state.transation.type == 'despesa'">
                                {{ store.state.transation.id != '' ? t('message.transactions.edit_expense') : t('message.transactions.new_expense') }}
                            </b>
                            <b v-else-if="store.state.transation.type == 'receita'">
                                {{ store.state.transation.id != '' ? t('message.transactions.edit_revenue') : t('message.transactions.new_revenue') }}
                            </b>
                            <b v-else>
                                {{ store.state.transation.id != '' ? t('message.transactions.edit_transfer') : t('message.transactions.new_transfer') }}
                            </b>
                    <a type="button" data-bs-dismiss="modal" @click="clearFields()" class="transaction-close-btn">
                                <i class="fal fa-times-circle fa-lg"></i>
                            </a>
                </div>
                <div class="modal-body transaction-modal__body" :class="darkMode ? 'darkmode' : ''">
                    <div class="col-11 mx-auto mt-3 mb-3 transaction-form-shell">

                        <div class="row">

                            <div class="col-12">
                                <div class="transaction-section-title">
                                    <span class="transaction-section-title__eyebrow">Etapa 1</span>
                                    <h6> Informações básicas</h6>
                                </div>
                            </div>

                            <div class="col-12 mb-3">
                                <label for="transactionDesc">{{ t('message.transactions.description') }}</label>
                                <input 
                                    type="text" 
                                    class="form-control form-control-lg transaction-field" 
                                    id="transactionDesc"
                                    name="transactionDesc"
                                    v-model="store.state.transation.description"
                                    v-bind:class="{
                                        'is-invalid': store.state.validation.descHasError,
                                        'darkmode': darkMode,
                                    }"
                                    @input="autoComplete"
                                >
                                <ul class="auto-complete-list transaction-autocomplete mt-2" v-if="store.state.suggestions.length > 0" v-bind:class="{
                                        'darkmode': darkMode,
                                        'bg-white': !darkMode
                                    }">
                                    <li class="pt-2 pb-2">
                                        <div class="row">
                                            <div class="col-9">
                                                <h6 class="text-sm">{{t('message.transactions.suggestions')}}</h6>
                                            </div>
                                            <div class="col-2 text-end">
                                                <a @click="closeAutoComplete"><i class="fas fa-times"></i></a>
                                            </div>
                                        </div>
                                    </li>
                                    <li class="pt-3 pb-3 text-sm" v-for="suggestion in store.state.suggestions" :key="suggestion.id" @click="selectAutoComplete(suggestion)">
                                        <div class="row">
                                            <div class="col-7">
                                                <img :src="suggestion.account_icon" alt="" width="25" class="rounded-5 me-2"> {{ suggestion.description }} - {{ suggestion.category_name }} 
                                            </div>
                                            <div class="col-4 text-end">
                                                {{formatMoney(suggestion.amount)}}
                                            </div>
                                        </div>
                                        
                                    </li>
                                </ul>
                                <div id="categoryNameFeedback" class="invalid-feedback">
                                    {{ store.state.validation.descMessageError }}
                                </div>
                            </div>

                            <div class="col-6 mb-3">
                                <label for="transactionAmounti">
                                    <div class="row">
                                        <div class="col-6">
                                            {{t('message.transactions.amount')}}
                                        </div>
                                        <div class="col-6">
                                            <calculator @updateValue="updateValue" :value="store.state.transaction_value.value"></calculator>
                                        </div>
                                    </div>                                        
                                </label>
                                <input 
                                    type="tel" 
                                    class="form-control form-control-lg transaction-field" 
                                    id="transactionAmount"
                                    name="transactionAmount"
                                    v-model.lazy="store.state.transaction_value.value" 
                                    v-money="money"
                                    v-bind:class="{
                                        'darkmode': darkMode,
                                    }"
                                >
                            </div>

                            <div class="col-6 mb-3">
                                <label for="transactionDate" class="transaction-date-label">{{t('message.transactions.date')}}</label>
                                <div class="transaction-date-wrap">
                                    <FlatPickr
                                        v-model="store.state.transation.date"
                                        :config="{
                                            altInput: true,
                                            altFormat: 'd/m/Y',
                                            dateFormat: 'Y-m-d',
                                            locale: Portuguese,
                                            allowInput: true,
                                            clickOpens: true
                                        }"
                                        class="form-control transaction-date-input"
                                        id="transactionDate"
                                    />
                                    <button
                                        type="button"
                                        class="transaction-date-trigger"
                                        @click.prevent="$event.currentTarget.parentElement.querySelector('input').focus()"
                                        tabindex="-1"
                                        aria-hidden="true"
                                    >
                                        <i class="fal fa-calendar-alt"></i>
                                    </button>
                                </div>
                                
                            </div>

                            <div class="col-12">
                                <div class="transaction-section-title transaction-section-title--spaced">
                                    <span class="transaction-section-title__eyebrow">Etapa 2</span>
                                    <h6> Classificação</h6>
                                </div>
                            </div>

                            <div v-if="store.state.transation.type == 'receita' || store.state.transation.type == 'despesa'" class="col-12 col-md-6 mb-3">
                                <label for="transactionAccount">{{t('message.transactions.account_card')}} <i class="fal fa-info-circle" v-tooltip="{
                                            content:t('message.transactions.account_card_tooltip'),
                                            distance: 15,
                                            html: true
                                        }"></i></label>
                                <select
                                    v-model="store.state.transation.account"
                                    class="form-select form-select-lg transaction-field"
                                >
                                    <option value="" disabled>Escolha uma opção</option>
                                    <option
                                        v-for="option in transactionAccountOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.name }}{{ option.meta ? ' - ' + option.meta : '' }}
                                    </option>
                                </select>
                                <div id="parentSelectFeedback" class="invalid-feedback d-block" v-if="store.state.validation.accountError">
                                    {{ store.state.validation.accountMessageError }}
                                </div>
                            </div>

                            <div v-if="store.state.transation.type == 'transferência'" class="col-12 col-md-6">
                                <label for="transactionAccountSender">{{ t('message.transactions.account_out') }}<i class="fal fa-info-circle"></i></label>
                                <select
                                    v-model="store.state.transation.account"
                                    class="form-select form-select-lg transaction-field"
                                    :class="{ 'darkmode': darkMode }"
                                >
                                    <option value="" disabled>{{ t('message.global.select_option') }}</option>
                                    <option
                                        v-for="option in bankOnlyOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.name }}
                                    </option>
                                </select>
                                <div class="invalid-feedback d-block" v-if="store.state.validation.accountError">
                                    {{ store.state.validation.accountMessageError }}
                                </div>
                            </div>

                            <div v-if="store.state.transation.type == 'transferência'" class="col-12 col-md-6">
                                <label for="transactionAccountDestiny">{{ t('message.transactions.account_in') }} <i class="fal fa-info-circle" v-tooltip="{
                                            content:'Para cadastrar uma nova conta<br>acesse o menu <b>configurações > Contas</b>',
                                            distance: 15,
                                            html: true
                                        }"></i></label>
                                <select
                                    v-model="store.state.transation.account_destiny"
                                    class="form-select form-select-lg transaction-field"
                                    :class="{ 'darkmode': darkMode }"
                                >
                                    <option value="" disabled>{{ t('message.global.select_option') }}</option>
                                    <option
                                        v-for="option in bankOnlyOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.name }}
                                    </option>
                                </select>
                            </div>

                            <div v-if="store.state.transation.type == 'receita' || store.state.transation.type == 'despesa'" class="col-12 col-md-6 mb-3">
                                <label for="categoryName">{{ t('message.menu.category') }} <i class="fal fa-info-circle" v-tooltip="{
                                            content:t('message.transactions.category_tooltip'),
                                            distance: 15,
                                            html: true
                                        }"></i></label>
                                <select
                                    v-model="store.state.transation.category"
                                    class="form-select form-select-lg transaction-field"
                                >
                                    <option value="" disabled>Escolha uma opção</option>
                                    <option
                                        v-for="option in categoryOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.name }}
                                    </option>
                                </select>
                                <div id="parentSelectFeedback" class="invalid-feedback d-block" v-if="store.state.validation.categorryError">
                                    {{ store.state.validation.categoryMessageError }}
                                </div>
                            </div>

                            <div v-if="!store.state.is_card && store.state.transation.type != 'transferência'" class="col-12">
                                <div class="transaction-section-title transaction-section-title--spaced">
                                    <span class="transaction-section-title__eyebrow">Etapa 3</span>
                                    <h6> Vínculos e destino</h6>
                                </div>
                            </div>

                            <div v-if="(store.state.transation.type == 'receita' || (store.state.transation.type == 'despesa' && !store.state.is_card))" class="col-12 mb-3">
                                <label class="transaction-switch" for="transactionObjetive">
                                    <input class="transaction-switch__input" type="checkbox" id="transactionObjetive" v-model="store.state.transation.objetive_checked" :disabled="store.state.transation.id != ''">
                                    <span class="transaction-switch__ui" aria-hidden="true"></span>
                                    <span class="transaction-switch__label">{{ store.state.transation.type == 'despesa' ? t('message.transactions.objetive_expense') : t('message.transactions.objetive_revenue') }}</span>
                                </label>
                            </div>

                            <div v-if="(store.state.transation.type == 'receita' || store.state.transation.type == 'despesa') && (store.state.transation.objetive_checked)" class="col-12 mb-3">
                                <label for="categoryName">{{ t('message.global.objetive') }} <i class="fal fa-info-circle" v-tooltip="{
                                            content:t('message.transactions.objetive_tooltip'),
                                            distance: 15,
                                            html: true
                                        }"></i></label>
                                <select
                                    v-model="store.state.transation.objetive"
                                    class="form-select form-select-lg transaction-field"
                                    :class="{ 'darkmode': darkMode }"
                                    :disabled="store.state.transation.id != ''"
                                >
                                    <option value="" disabled>{{ t('message.global.select_option') }}</option>
                                    <option
                                        v-for="option in objetiveOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.name }}
                                    </option>
                                </select>
                            </div>

                            <div v-if="(store.state.transation.type == 'receita' || store.state.transation.type == 'despesa') && (cookies.get('user_agive_account_type') === 'J')" class="col-12 mb-3">
                                <label for="customerName">{{ t('message.transactions.customer_stakeholder') }} <i class="fal fa-info-circle" v-tooltip="{
                                            content:t('message.transactions.customer_stakeholder_tooltip'),
                                            distance: 15,
                                            html: true
                                        }"></i></label>
                                <select
                                    v-model="store.state.transation.customer_supplier"
                                    class="form-select form-select-lg transaction-field"
                                    :class="{ 'darkmode': darkMode }"
                                >
                                    <option value="" disabled>{{ t('message.global.select_option') }}</option>
                                    <option
                                        v-for="option in customerSupplierOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        {{ option.name }}{{ option.meta ? ' - ' + option.meta : '' }}
                                    </option>
                                </select>
                            </div>

                            <div class="col-12">
                                <div class="transaction-section-title transaction-section-title--spaced">
                                    <span class="transaction-section-title__eyebrow">{{ store.state.is_card || store.state.transation.type == 'transferência' ? 'Etapa 3' : 'Etapa 4' }}</span>
                                    <h6> Detalhes adicionais</h6>
                                </div>
                            </div>

                            <div class="col-12 mb-3">
                                <label for="transactionObservation">{{ t('message.transactions.observation') }}</label>
                                <textarea
                                    id="transactionObservation"
                                    rows="4"
                                    class="form-control transaction-field transaction-textarea"
                                    v-model="store.state.transation.observation"
                                    v-bind:class="{
                                        'darkmode': darkMode,
                                    }"
                                ></textarea>
                            </div>

                            <div class="col-12">
                                <div class="transaction-section-title transaction-section-title--spaced">
                                    <span class="transaction-section-title__eyebrow">{{ store.state.is_card || store.state.transation.type == 'transferência' ? 'Etapa 4' : 'Etapa 5' }}</span>
                                    <h6>Recorrência e status</h6>
                                </div>
                            </div>

                            <div v-if="store.state.transation.id == ''" class="col-12 mb-3">
                                <label class="transaction-switch" for="transactionRecurrence">
                                    <input class="transaction-switch__input" type="checkbox" id="transactionRecurrence" v-model="store.state.transation.recurrence_checked">
                                    <span class="transaction-switch__ui" aria-hidden="true"></span>
                                    <span class="transaction-switch__label">{{ t('message.transactions.repeat') }}</span>
                                </label>
                            </div>

                            <div v-if="store.state.transation.recurrence_checked && store.state.transation.id == ''" class="col-12 mb-3">
                                <div class="form-check">
                                    <input class="form-check-input" type="radio" name="transactionRecurrenceType" id="transactionRecurrenceType1" value="Fixed" v-model="store.state.transation.recurrence">
                                    <label class="form-check-label" for="transactionRecurrenceType1">
                                        {{ store.state.transation.type == 'despesa' ? t('message.transactions.fixed_expense') : t('message.transactions.fixed_revenue')}}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input class="form-check-input" type="radio" name="transactionRecurrenceType" id="transactionRecurrenceType2" value="Installments" v-model="store.state.transation.recurrence">
                                    <label class="form-check-label" for="transactionRecurrenceType2">
                                        {{ store.state.transation.type == 'despesa' ? t('message.transactions.instaltement_expense') : t('message.transactions.instaltement_revenue')}}
                                    </label>
                                </div>
                            </div>

                            <div v-if="store.state.transation.recurrence_checked && store.state.transation.recurrence == 'Installments' && store.state.transation.id == ''" class="col-6 mb-3">

                                <label for="transactionInitialI">{{ t('message.transactions.initial_instaltement') }}</label>
                                <select class="form-select form-select-lg transaction-field"
                                    v-model="store.state.transation.initial_installment"
                                    v-bind:class="{
                                        'darkmode': darkMode,
                                    }"
                                >
                                    <option v-for="n in 640" :key="n"
                                        :value="n"
                                    >
                                        {{n}}
                                    </option>
                                </select>

                            </div>

                            <div v-if="store.state.transation.recurrence_checked && store.state.transation.recurrence == 'Installments' && store.state.transation.id == ''" class="col-6 mb-3">

                                <label for="transactionInitialI">{{ t('message.transactions.quantity_instaltement') }}</label>
                                <select class="form-select form-select-lg transaction-field"
                                    v-model="store.state.transation.end_installment"
                                    v-bind:class="{
                                        'darkmode': darkMode,
                                    }"
                                >
                                    <option v-for="n in 640" :key="n"
                                        :value="n"
                                    >
                                        {{n}}
                                    </option>
                                </select>

                            </div>

                            <div v-if="store.state.transation.recurrence_checked && store.state.transation.id == '' && (store.state.transation.recurrence == 'Fixed' || store.state.transation.recurrence == 'Installments')" class="col-12 mb-3">

                                <label for="transactionPeriod">{{ t('message.transactions.periocity') }}</label>
                                <select class="form-select form-select-lg transaction-field"
                                    v-model="store.state.transation.recurrence_period"
                                    v-bind:class="{
                                        'darkmode': darkMode,
                                    }"
                                >
                                    <option value="">{{ t('message.global.select_option') }}</option>
                                    <option value="day">{{ t('message.transactions.daily') }}</option>
                                    <option value="weekley">{{t('message.transactions.weekley')}}</option>
                                    <option value="biweekly">{{t('message.transactions.biweekly')}}</option>
                                    <option value="monthly">{{t('message.transactions.monthly')}}</option>
                                    <option value="quarterly">{{t('message.transactions.quarteyly')}}</option>
                                    <option value="semi-annual">{{ t('message.transactions.semi_annual') }}</option>
                                    <option value="annual">{{t('message.transactions.annual')}}</option>
                                </select>

                            </div>

                            <div v-if="store.state.transation.id != '' && store.state.transation.recurrence != '' && store.state.transation.recurrence != 'No'" class="col-11 mx-auto mb-3 rounded"
                            v-bind:class="{
                                        'bg-light': !darkMode,
                                    }">

                                <p class="text-danger fw-bold mt-2">
                                    {{ t('message.transactions.other_dates') }}
                                </p>
                                <div class="form-check">
                                    <input class="form-check-input" type="radio" name="transactionUpdateOthers" id="transactionUpdateOthers1" value="just_this" v-model="store.state.transation.howUpdate">
                                    <label class="form-check-label" for="transactionUpdateOthers1">
                                        {{ t('message.transactions.just_this') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input class="form-check-input" type="radio" name="transactionUpdateOthers" id="transactionUpdateOthers2" value="this_after" v-model="store.state.transation.howUpdate">
                                    <label class="form-check-label" for="transactionUpdateOthers2">
                                        {{ t('message.transactions.this_next') }}
                                    </label>
                                </div>
                                <div class="form-check">
                                    <input class="form-check-input" type="radio" name="transactionUpdateOthers" id="transactionUpdateOthers3" value="all" v-model="store.state.transation.howUpdate">
                                    <label class="form-check-label" for="transactionUpdateOthers3">
                                        {{ t('message.transactions.all') }}
                                    </label>
                                </div>
                            
                            </div>

                            <div class="col-12 mb-3" v-if="!store.state.is_card">
                                <label class="transaction-switch" for="transactionPayment">
                                    <input class="transaction-switch__input" type="checkbox" id="transactionPayment" v-model="store.state.transation.payed">
                                    <span class="transaction-switch__ui" aria-hidden="true"></span>
                                    <span class="transaction-switch__label">{{ store.state.transation.type == 'receita' ? t('message.transactions.payment_revenue') : t('message.transactions.payment_expense') }}</span>
                                </label>
                            </div>

                            
                            
                        </div>

                    </div>
                </div>
                <div class="modal-footer transaction-modal__footer">
                    <div class="col-12 text-center mb-n3">
                        <button
                            v-if="!store.state.savingTransaction"
                            @click="submitTransaction()"
                            class="btn transaction-submit-btn"
                            :class="['btn'+store.state.modalColor, `transaction-submit-btn--${store.state.transation.type}`]"
                        >
                            {{ t('message.transactions.add') }}
                        </button>
                        <img v-if="store.state.savingTransaction" :src="loadGif" alt="" width="50">
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="detailsTransaction" tabindex="-1" aria-labelledby="detailsTransaction" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-transaction-dialog">
            <div class="modal-content border-0 transaction-modal"
                v-bind:class="{
                        'darkmode': darkMode,
                        'border-danger': store.state.transation.type == 'despesa',
                        'border-success': store.state.transation.type == 'receita',
                        'border-gray': store.state.transation.type == 'transferencia'
                    }">

                <div class="modal-body transaction-modal__body transaction-details-body" :class="darkMode ? 'darkmode' : ''">
                    <div class="transaction-details-card">
                        <div class="transaction-details-card__top">
                            <div class="transaction-details-main">
                                <div class="transaction-details-icon-wrap">
                                    <span
                                        class="transaction-details-icon-badge"
                                        :style="{ color: store.state.transation.category_color, backgroundColor: `${store.state.transation.category_color}20` }"
                                    >
                                        <i :class="'fal ' + store.state.transation.category_icon + ' fa-2x'"></i>
                                    </span>
                                </div>

                                <div class="transaction-details-main__content">
                                    <div class="transaction-details-title">{{ store.state.transation.description }}</div>
                                    <div class="transaction-details-amount" :class="'text'+store.state.modalColor">
                                        {{ store.state.transation.type == 'despesa' ? '-' : '' }} {{ store.state.transation.amount }}
                                    </div>
                                </div>
                            </div>

                            <a type="button" data-bs-dismiss="modal" @click="clearFields()" class="transaction-close-btn">
                                <i class="fal fa-times-circle fa-lg"></i>
                            </a>
                        </div>

                        <div class="transaction-details-grid">
                            <div class="transaction-detail-item">
                                <span class="transaction-detail-item__label">{{ t('message.global.date') }}</span>
                                <span class="transaction-detail-item__value">{{ formatDate(store.state.transation.date) }}</span>
                            </div>

                            <div class="transaction-detail-item">
                                <span class="transaction-detail-item__label">{{ t('message.global.bank_account') }}</span>
                                <span class="transaction-detail-item__value">{{ store.state.transation.account_name }}</span>
                            </div>

                            <div class="transaction-detail-item">
                                <span class="transaction-detail-item__label">{{ t('message.menu.category') }}</span>
                                <span class="transaction-detail-item__value">{{ store.state.transation.category_name }}</span>
                            </div>

                            <div v-if="store.state.transation.customer_supplier > 0" class="transaction-detail-item">
                                <span class="transaction-detail-item__label">Cliente</span>
                                <span class="transaction-detail-item__value">{{ store.state.transation.customer_supplier_name }}</span>
                            </div>

                            <div v-if="store.state.transation.observation != ''" class="transaction-detail-item transaction-detail-item--full">
                                <span class="transaction-detail-item__label">{{ t('message.transactions.observation') }}</span>
                                <span class="transaction-detail-item__value" v-html="addLinebreaks(store.state.transation.observation)"></span>
                            </div>
                        </div>

                        <div v-if="store.state.transation.recurrence == 'Installments'" class="transaction-installment-strip">
                            <div class="transaction-installment-item">
                                <span class="transaction-installment-item__label">{{ t('message.transactions.instaltement') }}</span>
                                <span class="transaction-installment-item__value">{{store.state.transation.current_installment}}/{{store.state.transation.end_installment}}</span>
                            </div>
                            <div class="transaction-installment-item">
                                <span class="transaction-installment-item__label">{{store.state.transation.type == 'despesa' ? t('message.transactions.instaltement_paid_expense') : t('message.transactions.instaltement_paid_revenue')}}</span>
                                <span class="transaction-installment-item__value">{{ formatMoney(store.state.transation.amount_paid) }}</span>
                            </div>
                            <div class="transaction-installment-item">
                                <span class="transaction-installment-item__label">{{store.state.transation.type == 'despesa' ? t('message.transactions.instaltement_unpaid_expense') : t('message.transactions.instaltement_unpaid_revenue')}}</span>
                                <span class="transaction-installment-item__value text-danger">{{ formatMoney(store.state.transation.ammount_unpaid) }}</span>
                            </div>
                        </div>

                        <div class="transaction-details-actions">
                            <a
                                data-bs-toggle="modal"
                                data-bs-target="#addTransaction"
                                class="transaction-details-action"
                                v-bind:class="{ 'darkmode': darkMode }"
                                v-tooltip="{
                                    content:t('message.global.edit'),
                                    distance: 15
                                }"
                            >
                                <i class="fal fa-pencil"></i>
                            </a>

                            <a
                                data-bs-toggle="modal"
                                data-bs-target="#deleteTransaction"
                                class="transaction-details-action transaction-details-action--danger"
                                v-tooltip="{
                                    content:t('message.global.delete'),
                                    distance: 15
                                }"
                            >
                                <i class="fal fa-trash"></i>
                            </a>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>

    <div class="modal fade" id="deleteTransaction" tabindex="-1" aria-labelledby="deleteTransactionModal" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-dialog-centered modal-transaction-dialog">
            <div class="modal-content border-0 transaction-modal transaction-modal--danger"
                v-bind:class="{
                        'darkmode': darkMode
                    }">
                <div class="modal-body transaction-modal__body transaction-delete-body" :class="darkMode ? 'darkmode' : ''">
                    <div class="transaction-delete-card">
                        <div class="transaction-delete-icon">
                            <i class="fal fa-trash-alt"></i>
                        </div>

                        <div class="transaction-delete-title" v-html="t('message.transactions.delete_title', {msg: store.state.transation.description})"></div>

                        <div class="transaction-delete-subtitle">
                            {{ t('message.global.this_action_cannot_be_undone') }}
                        </div>

                        <div
                            v-if="store.state.transation.recurrence != '' && store.state.transation.recurrence != 'No'"
                            class="transaction-delete-options"
                        >
                            <button class="btn transaction-delete-btn transaction-delete-btn--soft" type="button"
                                @click="deleteTransation(store.state.transation.id, store.state.transation.transaction_id, 'just_this')">
                                {{ t('message.transactions.delete_just_this') }}
                            </button>

                            <button class="btn transaction-delete-btn transaction-delete-btn--danger" type="button"
                                @click="deleteTransation(store.state.transation.id, store.state.transation.transaction_id, 'this_after')">
                                {{ t('message.transactions.delete_this_next') }}
                            </button>
                        </div>

                        <div v-else class="transaction-delete-actions">
                            <button type="button" class="btn transaction-delete-btn transaction-delete-btn--ghost" data-bs-dismiss="modal">
                                {{ t('message.global.cancel') }}
                            </button>

                            <a class="btn transaction-delete-btn transaction-delete-btn--danger"
                                @click="deleteTransation(store.state.transation.id, store.state.transation.transaction_id, 'just_this')">
                                {{ t('message.global.delete') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="modal fade" id="payTransaction" tabindex="-1" aria-labelledby="payTransaction" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-dialog-centered modal-transaction-dialog">
            <div class="modal-content border-0 transaction-modal" v-bind:class="{
                        'darkmode': darkMode,
                        'border-danger': store.state.transation.type == 'despesa',
                        'border-success': store.state.transation.type == 'receita',
                        'border-gray': store.state.transation.type == 'transferencia'
                    }">
                <div class="modal-body transaction-modal__body transaction-pay-body" :class="darkMode ? 'darkmode' : ''">
                    <div class="transaction-pay-card">
                        <div class="transaction-pay-card__top">
                            <div class="transaction-pay-title-wrap">
                                <div class="transaction-pay-title">
                                    {{store.state.transation.type == 'despesa' ? t('message.transactions.paying_expense'): t('message.transactions.paying_revenue')}}
                                </div>
                                                            </div>

                            <a type="button" data-bs-dismiss="modal" @click="clearFields()" class="transaction-close-btn">
                                <i class="fal fa-times-circle fa-lg"></i>
                            </a>
                        </div>

                        <div class="transaction-pay-shell">
                            <div class="transaction-pay-summary">
                                <span class="transaction-pay-summary__badge"
                                    :style="{ color: store.state.transation.category_color, backgroundColor: `${store.state.transation.category_color}20` }">
                                    <i :class="'fas ' + store.state.transation.category_icon + ' fa-2x'"></i>
                                </span>
                                <div class="transaction-pay-summary__desc">{{ store.state.transation.description }}</div>
                            </div>

                            <div class="transaction-pay-form">
                                <div class="row">
                                    <div class="col-12 mb-3">
                                        <label for="transactionAmount">{{ t('message.transactions.amount') }}</label>
                                        <input 
                                            type="tel" 
                                            class="form-control form-control-lg transaction-field" 
                                            id="transactionAmount"
                                            name="transactionAmount"
                                            v-model.lazy="store.state.transaction_value.value" 
                                            v-money="money"
                                            v-bind:class="{
                                                'darkmode': darkMode,
                                            }"
                                        >
                                    </div>

                                    <div class="col-12 mb-3">
                                        <label for="transactionDate" class="transaction-date-label">{{ t('message.global.date') }}</label>
                                        <div class="transaction-date-wrap">
                                            <FlatPickr
                                                v-model="store.state.transation.date"
                                                :config="{
                                                    altInput: true,
                                                    altFormat: 'd/m/Y',
                                                    dateFormat: 'Y-m-d',
                                                    locale: Portuguese,
                                                    allowInput: true,
                                                    clickOpens: true
                                                }"
                                                class="form-control transaction-date-input"
                                                id="transactionDate"
                                            />
                                            <button
                                                type="button"
                                                class="transaction-date-trigger"
                                                @click.prevent="$event.currentTarget.parentElement.querySelector('input').focus()"
                                                tabindex="-1"
                                                aria-hidden="true"
                                            >
                                                <i class="fal fa-calendar-alt"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div v-if="store.state.transation.type == 'receita' || store.state.transation.type == 'despesa'" class="col-12 mb-3">
                                        <label for="transactionAccount">{{ t('message.transactions.account_card') }}</label>
                                        <select
                                            v-model="store.state.transation.account"
                                            class="form-select form-select-lg transaction-field"
                                            :class="{ 'darkmode': darkMode }"
                                        >
                                            <option value="" disabled>{{ t('message.global.select_option') }}</option>
                                            <option
                                                v-for="option in transactionAccountOptions"
                                                :key="option.value"
                                                :value="option.value"
                                            >
                                                {{ option.name }}{{ option.meta ? ' - ' + option.meta : '' }}
                                            </option>
                                        </select>
                                        <div id="parentSelectFeedback" class="invalid-feedback d-block" v-if="store.state.validation.parentMessageError">
                                            {{ store.state.validation.parentMessageError }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="transaction-pay-actions">
                            <button
                                v-if="!store.state.savingTransaction"
                                @click="submitTransaction()"
                                class="btn transaction-submit-btn"
                                :class="['btn'+store.state.modalColor, `transaction-submit-btn--${store.state.transation.type}`]"
                            >
                                {{store.state.transation.type == 'despesa' ? t('message.transactions.paying_button_expense') : t('message.transactions.paying_button_revenue')}}
                            </button>
                            <img v-if="store.state.savingTransaction" :src="loadGif" alt="" width="50">
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped>
.modal-transaction-dialog {
  max-width: 620px;
}

.transaction-modal {
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.16);
  background: #ffffff;
}

.transaction-modal::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  background: #f43f5e;
}

.border-success.transaction-modal::before {
  background: #10b981;
}

.border-warning.transaction-modal::before,
.border-gray.transaction-modal::before {
  background: #f59e0b;
}

.transaction-modal__header,
.transaction-modal__footer {
  border: 0;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(248, 250, 252, 0.72));
}

.transaction-modal__header {
  padding: 1.15rem 1.35rem 1rem;
  align-items: center;
}

.transaction-modal__header b {
  font-size: 1.15rem;
  line-height: 1.2;
  color: #334155;
}

.transaction-modal__body {
  padding: 0.35rem 0 0;
}

.transaction-modal__footer {
  padding: 1rem 1.35rem 1.35rem;
}

.transaction-form-shell,
.transaction-confirm-shell,
.transaction-summary-head,
.transaction-pay-shell {
  background: rgba(248, 250, 252, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 20px;
  padding: 1rem;
}

.transaction-form-shell .row > [class*="col-"] {
  margin-bottom: .15rem;
}

label {
  font-size: .9rem;
  font-weight: 700;
  color: #3b4d6b;
  margin-bottom: .45rem;
}

.transaction-field, .transaction-date-input {
  min-height: 54px;
  border-radius: 14px;
  border: 1px solid #d7deea;
  background: #fff !important;
  box-shadow: none;
  transition: border-color .18s ease, box-shadow .18s ease, transform .18s ease;
}

:deep(.form-control),
:deep(.form-select) {
    min-height: 54px;
    border-radius: 14px;
}

.transaction-textarea {
  min-height: 120px;
  resize: vertical;
}

.transaction-field:focus {
  border-color: rgba(244, 63, 94, .55);
  box-shadow: 0 0 0 4px rgba(244, 63, 94, .10);
}

.border-success .transaction-field:focus, .border-success .transaction-date-input:focus {
  border-color: rgba(16, 185, 129, .55) !important;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, .10) !important;
}

.border-warning .transaction-field:focus,
.border-gray .transaction-field:focus {
  border-color: rgba(245, 158, 11, .55);
  box-shadow: 0 0 0 4px rgba(245, 158, 11, .10);
}

.transaction-close-btn {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #d7deea;
  color: #64748b;
  text-decoration: none;
  transition: all .18s ease;
}

.transaction-close-btn:hover {
  transform: scale(1.04);
  color: #334155;
  border-color: #b9c5d8;
}



.transaction-switch {
  position: relative;
  display: flex;
  align-items: center;
  gap: .85rem;
  min-height: 58px;
  padding: .95rem 1rem;
  border-radius: 16px;
  background: rgba(255,255,255,.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  overflow: hidden;
  cursor: pointer;
}

.transaction-switch__input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.transaction-switch__ui {
  position: relative;
  width: 42px;
  min-width: 42px;
  height: 24px;
  border-radius: 999px;
  background: #cbd5e1;
  flex-shrink: 0;
  transition: background-color .22s ease, box-shadow .22s ease;
}

.transaction-switch__ui::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.18);
  transition: transform .22s ease;
}

.transaction-switch__input:checked + .transaction-switch__ui {
  background: #f43f5e;
}

.transaction-switch__input:checked + .transaction-switch__ui::after {
  transform: translateX(18px);
}

.border-success .transaction-switch__input:checked + .transaction-switch__ui {
  background: #10b981;
}

.border-warning .transaction-switch__input:checked + .transaction-switch__ui,
.border-gray .transaction-switch__input:checked + .transaction-switch__ui {
  background: #f59e0b;
}

.transaction-switch__input:focus-visible + .transaction-switch__ui {
  box-shadow: 0 0 0 .2rem rgba(244, 63, 94, .12);
}

.border-success .transaction-switch__input:focus-visible + .transaction-switch__ui {
  box-shadow: 0 0 0 .2rem rgba(16, 185, 129, .12);
}

.border-warning .transaction-switch__input:focus-visible + .transaction-switch__ui,
.border-gray .transaction-switch__input:focus-visible + .transaction-switch__ui {
  box-shadow: 0 0 0 .2rem rgba(245, 158, 11, .12);
}

.transaction-switch__input:disabled + .transaction-switch__ui {
  opacity: .65;
}

.transaction-switch__label {
  flex: 1;
  margin: 0;
  font-weight: 600;
  line-height: 1.4;
  color: #42556f;
}

.darkmode .transaction-switch {
  background: rgba(17, 28, 52, .75);
  border-color: rgba(148, 163, 184, 0.14);
}

.darkmode .transaction-switch__ui {
  background: #475569;
}

.darkmode .transaction-switch__ui::after {
  box-shadow: 0 2px 6px rgba(2, 6, 23, 0.45);
}

.darkmode .transaction-switch__label {
  color: #e5edf8;
}


.transaction-submit-btn {
  min-width: 150px;
  min-height: 46px;
  border-radius: 14px;
  font-weight: 700;
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
  padding-inline: 1.4rem;
}

.transaction-autocomplete {
  list-style: none;
  margin: 0;
  padding: .45rem;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
  max-height: 260px;
  overflow: auto;
}

.transaction-autocomplete li {
  border-radius: 12px;
  padding-left: .8rem;
  padding-right: .8rem;
}

.transaction-autocomplete li + li {
  margin-top: .2rem;
}

.transaction-autocomplete li:hover {
  background: rgba(244, 63, 94, .06);
  cursor: pointer;
}

.darkmode.transaction-modal,
.darkmode .transaction-form-shell,
.darkmode .transaction-confirm-shell,
.darkmode .transaction-summary-head,
.darkmode .transaction-pay-shell {
  background: #0f172a;
  color: #e5edf8;
  border-color: rgba(148, 163, 184, 0.14);
}

.darkmode.transaction-modal__header,
.darkmode.transaction-modal__footer {
  background: linear-gradient(180deg, rgba(15, 23, 42, .98), rgba(15, 23, 42, .9));
}

.darkmode.transaction-modal__header b,
.darkmode label,
.darkmode .transaction-switch .form-check-label {
  color: #e5edf8;
}

.darkmode .transaction-field,
.darkmode.transaction-field,
.darkmode .transaction-close-btn,
.darkmode .transaction-autocomplete {
  background: #111c34 !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
  color: #e5edf8 !important;
}

.darkmode .transaction-close-btn {
  color: #c7d3e6 !important;
}

.darkmode .transaction-switch {
  background: rgba(17, 28, 52, .75);
}




.transaction-section-title {
  display: flex;
  gap: .22rem;
  margin: .1rem 0 .65rem;
  padding: .1rem .1rem 0;
}

.transaction-section-title--spaced {
  margin-top: .35rem;
}

.transaction-section-title__eyebrow {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: .1rem .55rem;
  border-radius: 999px;
  background: rgba(244, 63, 94, .08);
  color: #e11d48;
  font-size: .7rem;
  font-weight: 800;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.border-success .transaction-section-title__eyebrow {
  background: rgba(16, 185, 129, .1);
  color: #059669;
}

.border-warning .transaction-section-title__eyebrow,
.border-gray .transaction-section-title__eyebrow {
  background: rgba(245, 158, 11, .12);
  color: #d97706;
}

.transaction-section-title h6 {
  margin: 0;
  font-size: .98rem;
  font-weight: 800;
  color: #334155;
}

.transaction-form-shell .transaction-section-title + [class*="col-"] label {
  margin-top: .05rem;
}

.darkmode .transaction-section-title h6 {
  color: #e5edf8;
}

.darkmode .transaction-section-title__eyebrow {
  background: rgba(148, 163, 184, .14);
  color: #f8fafc;
}

.transaction-date-wrap {
  position: relative;
}

.transaction-date-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  z-index: 2;
  pointer-events: none;
}

.transaction-date-input {
  padding-right: 2.8rem !important;
}

.transaction-option {
  display: flex;
  align-items: center;
  gap: .75rem;
  min-width: 0;
}

.transaction-option__image,
.transaction-option__icon-badge {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex: 0 0 34px;
}

.transaction-option__image {
  object-fit: cover;
  background: #fff;
  border: 1px solid #e2e8f0;
}

.transaction-option__icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eef4fb;
  color: #475569;
}

.transaction-option__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.transaction-option__title {
  font-weight: 600;
  color: #334155;
}

.transaction-option__meta {
  color: #64748b;
}

.transaction-multiselect {
  min-height: 58px;
  border-radius: 16px !important;
  border: 1px solid #dbe4f0 !important;
  background: rgba(255, 255, 255, 0.96) !important;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
}

.transaction-multiselect.is-active {
  border-color: rgba(244, 63, 94, .42) !important;
  box-shadow: 0 0 0 4px rgba(244, 63, 94, .10);
}

.border-success .transaction-multiselect.is-active {
  border-color: rgba(16, 185, 129, .42) !important;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, .10);
}

.border-warning .transaction-multiselect.is-active,
.border-gray .transaction-multiselect.is-active {
  border-color: rgba(245, 158, 11, .42) !important;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, .10);
}

.transaction-multiselect .multiselect-wrapper {
  min-height: 58px;
  padding: .55rem .9rem;
  box-shadow: none !important;
  background: transparent !important;
}

.transaction-multiselect .multiselect-single-label,
.transaction-multiselect .multiselect-search,
.transaction-multiselect .multiselect-placeholder {
  font-size: 1rem;
  color: #334155;
}

.transaction-multiselect .multiselect-placeholder {
  color: #7b8798;
}

.transaction-multiselect .multiselect-search {
  background: transparent !important;
}

.transaction-multiselect .multiselect-dropdown {
  margin-top: .45rem;
  border: 1px solid #dbe4f0;
  border-radius: 16px;
  box-shadow: 0 22px 45px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.transaction-multiselect .multiselect-option {
  padding: .75rem .9rem;
}

.transaction-multiselect .multiselect-option.is-pointed {
  background: rgba(244, 63, 94, .06);
}

.transaction-multiselect .multiselect-option.is-selected {
  background: rgba(244, 63, 94, .10);
}

.darkmode .transaction-option__title,
.darkmode .transaction-multiselect .multiselect-single-label,
.darkmode .transaction-multiselect .multiselect-search {
  color: #e5edf8 !important;
}

.darkmode .transaction-option__meta,
.darkmode .transaction-multiselect .multiselect-placeholder,
.darkmode .transaction-date-icon {
  color: #94a3b8 !important;
}

.darkmode .transaction-option__image {
  background: #0f172a;
  border-color: rgba(148, 163, 184, 0.18);
}

.darkmode .transaction-option__icon-badge,
.darkmode .transaction-multiselect,
.darkmode .transaction-multiselect .multiselect-dropdown {
  background: #111c34 !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
}

.darkmode .transaction-multiselect .multiselect-wrapper,
.darkmode .flatpickr-input {
  background: transparent !important;
}

.darkmode .transaction-multiselect .multiselect-option.is-pointed {
  background: rgba(148, 163, 184, .12);
}

.darkmode .transaction-multiselect .multiselect-option.is-selected {
  background: rgba(244, 63, 94, .18);
}

.flatpickr-calendar {
  border-radius: 18px;
  border: 1px solid #dbe4f0;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.16);
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange {
  background: #f43f5e;
  border-color: #f43f5e;
}

.border-success ~ .flatpickr-calendar .flatpickr-day.selected {
  background: #10b981;
  border-color: #10b981;
}

@media (max-width: 576px) {
  .modal-transaction-dialog {
    margin: .8rem;
  }

  .transaction-modal {
    border-radius: 20px;
  }

  .transaction-modal__header,
  .transaction-modal__footer {
    padding-inline: 1rem;
  }

  .transaction-form-shell,
  .transaction-confirm-shell,
  .transaction-summary-head,
  .transaction-pay-shell {
    padding: .9rem;
    border-radius: 16px;
  }
}

.transaction-option--selected {
    min-height: 42px;
}

.transaction-multiselect .multiselect-single-label {
    padding: 0 !important;
    display: flex;
    align-items: center;
    min-height: 42px;
}

.transaction-multiselect .multiselect-placeholder {
    color: #94a3b8;
}

.transaction-multiselect .multiselect-caret {
    background-image: none !important;
}

.transaction-multiselect .multiselect-caret:before {
    content: "\f107";
    font-family: "Font Awesome 5 Pro";
    font-weight: 300;
    font-size: 14px;
    color: #64748b;
}

.flatpickr-calendar {
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    box-shadow: 0 18px 50px rgba(15, 23, 42, 0.16);
    overflow: hidden;
}

.flatpickr-months {
    padding: 8px 8px 0;
}

.flatpickr-current-month {
    padding-top: 10px !important;
}

.flatpickr-weekdays {
    background: #f8fafc;
}

.flatpickr-day.today {
    border-color: #ff3b6b;
}



/* DETAILS MODAL */
.transaction-details-body {
  padding-top: .85rem;
}

.transaction-details-card {
  padding: 1rem 1rem 0;
}

.transaction-details-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0 0 .95rem;
}

.transaction-details-main {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
}

.transaction-details-icon-wrap {
  flex-shrink: 0;
}

.transaction-details-icon-badge {
  width: 72px;
  height: 72px;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.transaction-details-main__content {
  min-width: 0;
}

.transaction-details-title {
  font-size: 1.32rem;
  font-weight: 700;
  line-height: 1.2;
  color: #334155;
  margin-bottom: .2rem;
  word-break: break-word;
}

.transaction-details-amount {
  font-size: 1.42rem;
  font-weight: 800;
  line-height: 1.2;
}

.transaction-details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .9rem 1rem;
  padding: 1rem 0;
}

.transaction-detail-item {
  min-width: 0;
  padding: .95rem 1rem;
  border-radius: 16px;
  background: rgba(248, 250, 252, .9);
  border: 1px solid rgba(148, 163, 184, 0.14);
}

.transaction-detail-item--full {
  grid-column: 1 / -1;
}

.transaction-detail-item__label {
  display: block;
  margin-bottom: .35rem;
  font-size: .82rem;
  font-weight: 700;
  color: #64748b;
}

.transaction-detail-item__value {
  display: block;
  font-weight: 700;
  line-height: 1.35;
  color: #334155;
  word-break: break-word;
}

.transaction-installment-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0 -1rem;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
}

.transaction-installment-item {
  padding: 1rem .9rem;
  text-align: center;
}

.transaction-installment-item + .transaction-installment-item {
  border-left: 1px solid rgba(148, 163, 184, 0.16);
}

.transaction-installment-item__label {
  display: block;
  margin-bottom: .35rem;
  font-size: .82rem;
  font-weight: 700;
  color: #64748b;
}

.transaction-installment-item__value {
  display: block;
  font-weight: 800;
  color: #334155;
}

.transaction-details-actions {
  display: flex;
  justify-content: flex-end;
  gap: .65rem;
  padding: .95rem 0 1rem;
}

.transaction-details-action {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 252, .92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  color: #64748b;
  transition: all .2s ease;
}

.transaction-details-action:hover {
  color: #334155;
  transform: translateY(-1px);
}

.transaction-details-action--danger {
  color: #f43f5e;
}

.transaction-details-action--danger:hover {
  color: #e11d48;
}

.darkmode .transaction-details-title,
.darkmode .transaction-detail-item__value,
.darkmode .transaction-installment-item__value {
  color: #e5edf8;
}

.darkmode .transaction-detail-item,
.darkmode .transaction-details-action {
  background: rgba(17, 28, 52, .72);
  border-color: rgba(148, 163, 184, 0.14);
}

.darkmode .transaction-detail-item__label,
.darkmode .transaction-installment-item__label {
  color: #94a3b8;
}

.darkmode .transaction-details-card__top,
.darkmode .transaction-installment-strip,
.darkmode .transaction-installment-item + .transaction-installment-item {
  border-color: rgba(148, 163, 184, 0.14);
}

.darkmode .transaction-details-action {
  color: #c7d3e6;
}

@media (max-width: 576px) {
  .transaction-details-card {
    padding: .85rem .85rem 0;
  }

  .transaction-details-card__top {
    gap: .75rem;
  }

  .transaction-details-main {
    align-items: flex-start;
  }

  .transaction-details-icon-badge {
    width: 60px;
    height: 60px;
    border-radius: 18px;
  }

  .transaction-details-title {
    font-size: 1.1rem;
  }

  .transaction-details-amount {
    font-size: 1.18rem;
  }

  .transaction-details-grid {
    grid-template-columns: 1fr;
    gap: .75rem;
  }

  .transaction-installment-strip {
    grid-template-columns: 1fr;
    margin: 0 -.85rem;
  }

  .transaction-installment-item + .transaction-installment-item {
    border-left: 0;
    border-top: 1px solid rgba(148, 163, 184, 0.16);
  }
}



/* DELETE MODAL */
.transaction-delete-body {
  padding-top: .95rem;
}

.transaction-delete-card {
  padding: 1.1rem 1rem 1rem;
  text-align: center;
}

.transaction-delete-icon {
  width: 74px;
  height: 74px;
  border-radius: 22px;
  margin: 0 auto .95rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(244, 63, 94, .10);
  color: #f43f5e;
  font-size: 1.65rem;
}

.transaction-delete-title {
  color: #334155;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.35;
  margin-bottom: .45rem;
}

.transaction-delete-subtitle {
  color: #64748b;
  font-size: .95rem;
  line-height: 1.45;
  margin-bottom: 1.1rem;
}

.transaction-delete-options,
.transaction-delete-actions {
  display: grid;
  gap: .75rem;
}

.transaction-delete-actions {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.transaction-delete-btn {
  min-height: 46px;
  border-radius: 14px;
  font-weight: 700;
  padding: .8rem 1rem;
  border: 1px solid transparent;
  box-shadow: none !important;
}

.transaction-delete-btn--soft {
  background: rgba(248, 250, 252, .96);
  border-color: rgba(148, 163, 184, 0.16);
  color: #475569;
}

.transaction-delete-btn--soft:hover {
  background: rgba(241, 245, 249, 1);
  color: #334155;
}

.transaction-delete-btn--ghost {
  background: rgba(248, 250, 252, .96);
  border-color: rgba(148, 163, 184, 0.16);
  color: #475569;
}

.transaction-delete-btn--ghost:hover {
  background: rgba(241, 245, 249, 1);
  color: #334155;
}

.transaction-delete-btn--danger {
  background: #f43f5e;
  border-color: #f43f5e;
  color: #fff;
}

.transaction-delete-btn--danger:hover {
  background: #e11d48;
  border-color: #e11d48;
  color: #fff;
}

.darkmode .transaction-delete-title {
  color: #e5edf8;
}

.darkmode .transaction-delete-subtitle {
  color: #94a3b8;
}

.darkmode .transaction-delete-icon {
  background: rgba(244, 63, 94, .16);
}

.darkmode .transaction-delete-btn--soft,
.darkmode .transaction-delete-btn--ghost {
  background: rgba(17, 28, 52, .72);
  border-color: rgba(148, 163, 184, 0.14);
  color: #e5edf8;
}

@media (max-width: 576px) {
  .transaction-delete-card {
    padding: 1rem .85rem .9rem;
  }

  .transaction-delete-icon {
    width: 66px;
    height: 66px;
    border-radius: 18px;
    margin-bottom: .85rem;
  }

  .transaction-delete-actions {
    grid-template-columns: 1fr;
  }
}



/* PAY MODAL */
.transaction-pay-body {
  padding-top: .85rem;
}

.transaction-pay-card {
  padding: 1rem 1rem 1rem;
}

.transaction-pay-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: .9rem;
}

.transaction-pay-title {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  color: #334155;
}

.transaction-pay-subtitle {
  margin-top: .3rem;
  color: #64748b;
  font-weight: 600;
}

.transaction-pay-shell {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
  padding-top: 1rem;
}

.transaction-pay-summary {
  padding: 1rem;
  border-radius: 18px;
  background: rgba(248, 250, 252, .92);
  border: 1px solid rgba(148, 163, 184, 0.14);
  text-align: center;
}

.transaction-pay-summary__badge {
  width: 74px;
  height: 74px;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: .75rem;
}

.transaction-pay-summary__desc {
  font-weight: 700;
  color: #334155;
  line-height: 1.35;
  word-break: break-word;
}

.transaction-pay-form {
  min-width: 0;
}

.transaction-pay-form .row {
  --bs-gutter-x: 1rem;
  margin-left: 0;
  margin-right: 0;
}

.transaction-pay-form .row > [class*="col-"] {
  padding-left: 0;
  padding-right: 0;
}

.transaction-pay-actions {
  display: flex;
  justify-content: center;
  padding-top: .95rem;
}

.darkmode .transaction-pay-title,
.darkmode .transaction-pay-summary__desc {
  color: #e5edf8;
}

.darkmode .transaction-pay-subtitle {
  color: #94a3b8;
}

.darkmode .transaction-pay-card__top {
  border-color: rgba(148, 163, 184, 0.14);
}

.darkmode .transaction-pay-summary {
  background: rgba(17, 28, 52, .72);
  border-color: rgba(148, 163, 184, 0.14);
}

.transaction-date-wrap {
    position: relative;
}

.transaction-date-input {
    padding-right: 3.2rem !important;
}

.transaction-date-wrap .flatpickr-input[readonly],
.transaction-date-wrap input[readonly] {
    background: #ffffff;
}

.transaction-date-trigger {
    position: absolute;
    right: 0.8rem;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #607392;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.transaction-date-trigger:hover {
    background: #eef4fb;
    color: #213E60;
}

.darkmode .transaction-date-wrap .flatpickr-input[readonly],
.darkmode .transaction-date-wrap input[readonly],
.darkmode .transaction-date-trigger {
    background: #1e2d42;
    color: #f4f7fb;
}

@media (max-width: 576px) {
  .transaction-pay-card {
    padding: .85rem;
  }

  .transaction-pay-shell {
    grid-template-columns: 1fr;
    gap: .85rem;
  }

  .transaction-pay-summary {
    padding: .9rem;
  }

  .transaction-pay-summary__badge {
    width: 62px;
    height: 62px;
    border-radius: 18px;
    margin-bottom: .6rem;
  }
}

.transaction-date-label{
    margin-bottom: 10px !important;
}

</style>